import type { Animal } from "./types"

// This function simulates fetching all animals from an API
export async function fetchAnimals(): Promise<Animal[]> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 800))

  // Generate 10 animals with different data
  const animals = Array.from({ length: 10 }, (_, i) => {
    const baseAnimal = getMockAnimalData()

    // Modify some properties to make each animal unique
    return {
      ...baseAnimal,
      _id: { $oid: `animal_${i + 1}` },
      name: `${getRandomName()} ${getRandomLastName()}`,
      dgr: `${100 + i}`,
      age: Math.floor(Math.random() * 36), // 0-36 months
      weight: 20 + Math.random() * 80, // 20-100 kg
      adg: 50 + Math.random() * 100, // 50-150 g/day
      animalStatus: Math.random() > 0.2 ? "active" : "inactive",
      farm: ["east", "west", "north", "south"][Math.floor(Math.random() * 4)],
      coatColor: ["Charolais", "Black Angus", "Hereford", "Holstein"][Math.floor(Math.random() * 4)],
      birthDate: new Date(Date.now() - Math.random() * 3 * 365 * 24 * 60 * 60 * 1000).toISOString(), // Random date in the last 3 years
    }
  })

  return animals
}

// This function simulates fetching a single animal by ID
export async function getAnimalById(id: string): Promise<Animal | null> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 600))

  // In a real app, you would fetch this from an API
  // For this example, we'll return mock data
  const baseAnimal = getMockAnimalData()

  return {
    ...baseAnimal,
    _id: { $oid: id },
  }
}

// Helper function to get a random name
function getRandomName() {
  const names = [
    "Nola",
    "Max",
    "Bella",
    "Charlie",
    "Luna",
    "Cooper",
    "Stella",
    "Milo",
    "Lucy",
    "Bailey",
    "Daisy",
    "Rocky",
    "Molly",
    "Bear",
    "Maggie",
    "Duke",
  ]
  return names[Math.floor(Math.random() * names.length)]
}

// Helper function to get a random last name
function getRandomLastName() {
  const lastNames = [
    "Berg",
    "Smith",
    "Johnson",
    "Williams",
    "Brown",
    "Jones",
    "Miller",
    "Davis",
    "Garcia",
    "Rodriguez",
    "Wilson",
    "Martinez",
    "Anderson",
    "Taylor",
    "Thomas",
    "Moore",
  ]
  return lastNames[Math.floor(Math.random() * lastNames.length)]
}

// Mock data for a single animal
function getMockAnimalData(): Animal {
  return {
    _id: {
      $oid: "681b1f7a21fc53e3918f9b9b",
    },
    dgr: "437",
    birthDate: "2025-05-05T21:00:00.000Z",
    sex: "Female",
    name: "Nola Berg",
    age: 0,
    adg: 85.24,
    imageUrls: [
      "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1746607877981-_000db9d6-512e-4da1-a42f-302d78d20ac1.jpeg?alt=media&token=aeaa5b59-6577-476b-860a-0cc836232a30",
      "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1746607877981-_000db9d6-512e-4da1-a42f-302d78d20ac1.jpeg?alt=media&token=aeaa5b59-6577-476b-860a-0cc836232a30",
      "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1746607877981-_00a38441-fddc-43e8-8062-96a9f2a4e6ba.jpeg?alt=media&token=f4c24986-e161-4707-b07f-0c98622f9598",
      "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1746607877980-_0000fb58-c1f8-401c-be07-92643ef4bbb6.jpeg?alt=media&token=d78827aa-b362-41d8-a1ca-521ec56e976b",
      "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1746607877981-_0000fb58-c1f8-401c-be07-92643ef4bbb6.jpeg?alt=media&token=89493293-f43b-4e15-b40a-482ddff05a20",
      "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1746607877981-_0000fb58-c1f8-401c-be07-92643ef4bbb6.jpeg?alt=media&token=89493293-f43b-4e15-b40a-482ddff05a20",
      "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1746607917032-_000db9d6-512e-4da1-a42f-302d78d20ac1.jpeg?alt=media&token=2bcaa8e9-46f5-45ed-9993-4f9e314e97ed",
    ],
    videoUrls: [],
    w120: 26.1,
    ww: 57.5,
    pw: 50.5,
    yw: 1978.7,
    pmwg: 5.4,
    coatColor: "Charolais",
    size: "Large Frame",
    weight: 26.8,
    paternalNucleus: "Impedit consequatur",
    paternalType: "Synthetic",
    breedingType: "Line Breeding",
    vaccination: ["brucella"],
    racialComposition: "1/2",
    birthCondition: "stillborn",
    damName: "Fredericka Potter",
    damDGR: "Dolorem vitae autem ",
    damBreed: "Guzerat",
    sireDGR: "Consequatur Vitae i",
    sireName: "Vivien Mccormick",
    sireBreed: "Senepol",
    paternalGrandDamDGR: "Qui consequat Esse ",
    paternalGrandDamName: "Malachi Riggs",
    paternalGrandDamBreed: "Guzerat",
    paternalGrandSireDGR: "Consectetur nisi di",
    paternalGrandSireName: "Thomas Flowers",
    paternalGrandSireBreed: "Unknown Breed",
    maternalGrandDamDGR: "Consectetur odit mai",
    maternalGrandDamName: "Emerald Raymond",
    maternalGrandDamBreed: "Guzerá",
    maternalGrandSireDGR: "Ea excepturi officii",
    maternalGrandSireName: "Orla Parks",
    maternalGrandSireBreed: "Senepol",
    animalStatus: "active",
    birthWeight: 99.9,
    farmerID: "Iste cumque dolore o",
    farmID: "Animi laudantium e",
    url: "Assumenda perspiciat",
    farm: "west",
    assignedSupervisor: "ahmed",
    cattleClass: "calf",
    phases: "phase2",
    slaughterInfo: "Quam alias in repudi",
    diagnosis: "reproductive",
    inseminationProgram: "natural",
    numberOffspring: 162,
    indexEfficiency: 30.22,
    evaluation: "UnAssigned",
    phaseIStatus: false,
    phaseIIStatus: false,
    phaseIIIStatus: false,
    length_of_rump: 29.1,
    height_of_rump: 97.9,
    lower_height: 9.6,
    back_height: 55.4,
    body_length: 87.3,
    rib_depth: 32.6,
    chest_perimeter: 91.7,
    body_width: 54.1,
    scrotal_circumference: 68.8,
    createdAt: {
      $date: "2025-05-07T08:53:14.830Z",
    },
    updatedAt: {
      $date: "2025-05-07T08:53:14.830Z",
    },
    __v: 0,
  }
}
